import { z } from 'zod';
import { observationKindSchema } from './dataset.ts';
import { jsonObjectSchema, jsonValueSchema } from './json.ts';

export const componentKeySchema = z.string().regex(
  /^[a-z0-9][a-z0-9-]*(?:\.[a-z0-9][a-z0-9-]*)+@\d+$/,
  '组件键必须使用 namespace.name@version 格式',
);

export const componentCategorySchema = z.enum([
  'simulation',
  'chart',
  'formula',
  'text',
  'information',
  'control',
  'question',
  'annotation',
  'media',
  'layout',
  'feedback',
  'other',
]);

export const componentInputDescriptorSchema = z.object({
  name: z.string().min(1),
  description: z.string().default(''),
  required: z.boolean().default(false),
  multiple: z.boolean().default(false),
  acceptedDatasetKinds: z.array(observationKindSchema).default([]),
}).strict();

export const componentOutputDescriptorSchema = z.object({
  name: z.string().min(1),
  description: z.string().default(''),
  datasetKind: observationKindSchema.optional(),
}).strict();

export const componentEventDescriptorSchema = z.object({
  type: z.string().min(1),
  description: z.string().default(''),
  payloadSchema: jsonObjectSchema.optional(),
  configurable: z.boolean().optional(),
}).strict();

export const componentLayoutConstraintsSchema = z.object({
  /** 组件仍可读、可操作的最小尺寸，单位为 px。 */
  minWidth: z.number().positive().default(220),
  minHeight: z.number().positive().default(140),
  /** 组件继续放大不再有收益的合理上限，单位为 px。 */
  maxWidth: z.number().positive().optional(),
  maxHeight: z.number().positive().optional(),
  /** 组件内容最自然的宽高比，无单位。 */
  preferredAspectRatio: z.number().positive().optional(),
}).strict().superRefine((layout, context) => {
  if (layout.maxWidth !== undefined && layout.maxWidth < layout.minWidth) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['maxWidth'],
      message: '组件最大宽度不能小于最小宽度',
    });
  }
  if (layout.maxHeight !== undefined && layout.maxHeight < layout.minHeight) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['maxHeight'],
      message: '组件最大高度不能小于最小高度',
    });
  }
});

export const componentRuntimeRequirementSchema = z.object({
  capability: z.string().min(1),
  required: z.boolean().default(true),
  description: z.string().default(''),
}).strict();

export const componentDescriptorSchema = z.object({
  key: componentKeySchema,
  category: componentCategorySchema,
  title: z.string().min(1),
  description: z.string().default(''),
  capabilities: z.array(z.string().min(1)).max(100).default([]),
  suitableFor: z.array(z.string().min(1)).max(100).default([]),
  inputs: z.array(componentInputDescriptorSchema).max(100).default([]),
  outputs: z.array(componentOutputDescriptorSchema).max(100).default([]),
  events: z.array(componentEventDescriptorSchema).max(100).default([]),
  constraints: z.array(z.string().min(1)).default([]),
  configSchemaId: z.string().min(1),
  configSchema: jsonObjectSchema.default({}),
  defaultConfig: jsonObjectSchema.default({}),
  examples: z.array(jsonObjectSchema).default([]),
  notes: z.array(z.string().min(1)).default([]),
  layout: componentLayoutConstraintsSchema.default({
    minWidth: 220,
    minHeight: 140,
  }),
  runtimeRequirements: z.array(componentRuntimeRequirementSchema).default([]),
  metadata: z.record(z.string(), jsonValueSchema).default({}),
}).strict().superRefine((descriptor, context) => {
  const uniqueAt = (
    values: string[],
    path: 'capabilities' | 'suitableFor' | 'inputs' | 'outputs' | 'events',
  ) => {
    const seen = new Set<string>();
    for (const [index, value] of values.entries()) {
      if (seen.has(value)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [path, index],
          message: `组件描述存在重复项：${value}`,
        });
      }
      seen.add(value);
    }
  };
  uniqueAt(descriptor.capabilities, 'capabilities');
  uniqueAt(descriptor.suitableFor, 'suitableFor');
  uniqueAt(descriptor.inputs.map(item => item.name), 'inputs');
  uniqueAt(descriptor.outputs.map(item => item.name), 'outputs');
  uniqueAt(descriptor.events.map(item => item.type), 'events');
  if (descriptor.inputs.filter(input => input.multiple).length > 1) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['inputs'],
      message: '一个组件最多声明一个可重复输入',
    });
  }
});

export type ComponentCategory = z.infer<typeof componentCategorySchema>;
export type ComponentDescriptor = z.infer<typeof componentDescriptorSchema>;
export type ComponentInputDescriptor = z.infer<
  typeof componentInputDescriptorSchema
>;
export type ComponentOutputDescriptor = z.infer<
  typeof componentOutputDescriptorSchema
>;
export type ComponentEventDescriptor = z.infer<
  typeof componentEventDescriptorSchema
>;
export type ComponentLayoutConstraints = z.infer<
  typeof componentLayoutConstraintsSchema
>;
export type ComponentRuntimeRequirement = z.infer<
  typeof componentRuntimeRequirementSchema
>;
